import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Navbar from "@/components/Navbar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function ProfilePage({ user, onLogout, onUserUpdate }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState({
    name: user.name || "",
    bio: user.bio || "",
    avatar_url: user.avatar_url || ""
  });
  const [badges, setBadges] = useState([]);
  const [allBadges, setAllBadges] = useState([]);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadBadges();
  }, []);

  const loadBadges = async () => {
    try {
      const response = await axios.get(`${API}/gamification/badges`);
      setAllBadges(response.data);
      setBadges(user.badges || []);
    } catch (err) {
      console.error("Error loading badges:", err);
    }
  };

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");

    try {
      const response = await axios.put(`${API}/auth/profile`, profile);
      onUserUpdate(response.data);
      setMessage("Profile updated successfully! 🎉");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error updating profile:", err);
      setMessage("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const getAvatarDisplay = () => {
    if (profile.avatar_url) {
      return (
        <img
          src={profile.avatar_url}
          alt="Avatar"
          className="w-32 h-32 rounded-full object-cover border-4 border-yellow-400"
        />
      );
    }
    return (
      <div className="w-32 h-32 rounded-full bg-gradient-to-br from-yellow-400 to-orange-400 flex items-center justify-center text-white text-5xl font-bold border-4 border-yellow-400">
        {profile.name.charAt(0).toUpperCase()}
      </div>
    );
  };

  const getBadgeDetails = (badgeId) => {
    return allBadges.find(b => b.id === badgeId);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-yellow-50 to-orange-50">
      <Navbar user={user} onLogout={onLogout} />

      <div className="container mx-auto px-4 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate("/dashboard")}
          className="mb-6 text-gray-600 hover:text-gray-800 font-semibold flex items-center gap-2"
          data-testid="back-to-dashboard-btn"
        >
          ← Back to Dashboard
        </button>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Profile Card */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <div className="flex flex-col items-center mb-6">
                {getAvatarDisplay()}
                <h2 className="text-2xl font-bold text-gray-800 mt-4">{user.name}</h2>
                <p className="text-gray-600 capitalize">{user.role}</p>
                <p className="text-sm text-gray-500">{user.email}</p>
              </div>

              {user.role === "student" && (
                <div className="bg-gradient-to-r from-yellow-100 to-orange-100 rounded-lg p-4 text-center">
                  <div className="text-4xl font-bold text-yellow-700">{user.points || 0}</div>
                  <div className="text-sm text-gray-700 font-semibold">Total Points</div>
                </div>
              )}
            </div>

            {/* Badges Section for Students */}
            {user.role === "student" && (
              <div className="bg-white rounded-2xl shadow-lg p-6 mt-6">
                <h3 className="text-xl font-bold text-gray-800 mb-4">
                  My Badges ({badges.length}/{allBadges.length})
                </h3>
                <div className="grid grid-cols-2 gap-3">
                  {allBadges.map((badge) => {
                    const earned = badges.includes(badge.id);
                    return (
                      <div
                        key={badge.id}
                        className={`p-3 rounded-lg text-center transition-all ${
                          earned
                            ? "bg-yellow-100 border-2 border-yellow-400"
                            : "bg-gray-100 opacity-50"
                        }`}
                        title={badge.description}
                      >
                        <div className="text-3xl mb-1">{badge.icon}</div>
                        <div className={`text-xs font-semibold ${earned ? "text-yellow-800" : "text-gray-500"}`}>
                          {badge.name}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Edit Profile Form */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-lg p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Edit Profile</h2>

              {message && (
                <div className={`mb-6 p-4 rounded-lg ${
                  message.includes("success") ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                }`}>
                  {message}
                </div>
              )}

              <div className="space-y-6">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Full Name</label>
                  <input
                    type="text"
                    name="name"
                    value={profile.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:outline-none focus:border-yellow-400"
                    data-testid="name-input"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Avatar URL</label>
                  <input
                    type="text"
                    name="avatar_url"
                    value={profile.avatar_url}
                    onChange={handleChange}
                    placeholder="https://example.com/avatar.jpg"
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:outline-none focus:border-yellow-400"
                    data-testid="avatar-url-input"
                  />
                  <p className="text-sm text-gray-500 mt-1">Enter a URL to your profile picture</p>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">Bio</label>
                  <textarea
                    name="bio"
                    value={profile.bio}
                    onChange={handleChange}
                    placeholder="Tell us about yourself..."
                    className="w-full px-4 py-3 rounded-lg border-2 border-gray-300 focus:outline-none focus:border-yellow-400 min-h-[120px]"
                    data-testid="bio-input"
                  />
                </div>

                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="lemon-button w-full py-3 rounded-lg text-white font-semibold disabled:opacity-50"
                  data-testid="save-profile-btn"
                >
                  {saving ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </div>

            {/* Badge Details for Students */}
            {user.role === "student" && allBadges.length > 0 && (
              <div className="bg-white rounded-2xl shadow-lg p-8 mt-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">Badge Collection</h3>
                <div className="space-y-4">
                  {allBadges.map((badge) => {
                    const earned = badges.includes(badge.id);
                    return (
                      <div
                        key={badge.id}
                        className={`flex items-start gap-4 p-4 rounded-lg border-2 ${
                          earned ? "border-yellow-400 bg-yellow-50" : "border-gray-200 bg-gray-50"
                        }`}
                      >
                        <div className="text-5xl">{badge.icon}</div>
                        <div className="flex-1">
                          <h4 className={`font-bold text-lg ${earned ? "text-yellow-800" : "text-gray-500"}`}>
                            {badge.name}
                            {earned && <span className="ml-2 text-sm">✓</span>}
                          </h4>
                          <p className="text-sm text-gray-600">{badge.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ProfilePage;
